import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

const NotFound = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Define old URL to new URL mappings
    const redirectMap = {
      '/used-cars': '/inventory',
      '/pre-owned': '/inventory',
      '/auto-inventory': '/inventory',
      '/car-inventory': '/inventory',
      '/trucks-for-sale': '/inventory',
      '/suvs-for-sale': '/inventory',
      '/service-department': '/service',
      '/auto-repair': '/service',
      '/maintenance': '/service',
      '/schedule-service': '/service',
      '/golf-carts-for-sale': '/golf-carts',
      '/used-golf-carts': '/golf-carts',
      '/custom-golf-carts': '/golf-carts',
      '/auto-financing': '/finance-application',
      '/car-loans': '/finance-application',
      '/credit-application': '/finance-application',
      '/finance-center': '/finance-application',
      '/financing': '/finance-application',
      '/about-us': '/about',
      '/our-team': '/about',
      '/dealership-info': '/about',
      '/contact-us': '/contact',
      '/hours-directions': '/contact',
      '/location': '/contact',
      '/specials': '/deals',
      '/current-offers': '/deals',
      '/promotions': '/deals',
      '/clearance': '/deals',
      '/wp-admin': '/admin',
      '/dashboard': '/admin',
      '/dealer-login': '/admin'
    };

    // Check if current path matches any old URLs
    const currentPath = location.pathname.toLowerCase();
    
    // Direct match
    if (redirectMap[currentPath]) {
      navigate(redirectMap[currentPath], { replace: true });
      return;
    }

    // Check for partial matches (e.g., /blog/post-title -> /news)
    if (currentPath.startsWith('/blog/') || currentPath.startsWith('/articles/')) {
      navigate('/news', { replace: true });
      return;
    }

    // Check for old PHP/HTML extensions
    const pathWithoutExtension = currentPath.replace(/\.(php|html|htm|aspx)$/, '');
    if (redirectMap[pathWithoutExtension]) {
      navigate(redirectMap[pathWithoutExtension], { replace: true });
      return;
    }
    
    // Log 404s to track missing redirects
    console.log('404 - Page not found:', currentPath);
    // Log 404 for analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: '404 Not Found',
        page_location: window.location.href,
        page_path: window.location.pathname
      });
    }
  }, [location, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center px-6"
      >
        <h1 className="text-9xl font-bold text-primary-600 mb-4">404</h1>
        <h2 className="text-3xl font-semibold text-gray-800 mb-4">
          Page Not Found
        </h2>
        <p className="text-gray-600 mb-8 max-w-md mx-auto">
          The page you're looking for doesn't exist or has been moved. 
          Let's get you back on track!
        </p>
        
        <div className="space-y-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/')}
            className="bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
          >
            Go to Homepage
          </motion.button>
          
          <div className="flex justify-center space-x-4 mt-6">
            <button
              onClick={() => navigate('/inventory')}
              className="text-primary-600 hover:text-primary-800 underline"
            >
              Browse Inventory
            </button>
            <button
              onClick={() => navigate('/contact')}
              className="text-primary-600 hover:text-primary-800 underline"
            >
              Contact Us
            </button>
          </div>
        </div>

        {/* Helpful suggestions based on common 404s */}
        <div className="mt-12 p-6 bg-white rounded-lg shadow-md max-w-2xl mx-auto">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Were you looking for one of these?
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <a href="/inventory" className="text-primary-600 hover:underline">
              Vehicle Inventory
            </a>
            <a href="/golf-carts" className="text-primary-600 hover:underline">
              Golf Carts
            </a>
            <a href="/service" className="text-primary-600 hover:underline">
              Service Department
            </a>
            <a href="/finance-application" className="text-primary-600 hover:underline">
              Financing Options
            </a>
            <a href="/deals" className="text-primary-600 hover:underline">
              Current Deals
            </a>
            <a href="/about" className="text-primary-600 hover:underline">
              About Us
            </a>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default NotFound;
